/* Knowledge base — client profile workspace.
   Grid entry point → full canonical markdown profile with Profile / Edit draft /
   Diff / History tabs. Devs propose edits (never overwrite); edits become a
   pending change request for Admin › KB approvals. Reads window.MAV.kbProfiles. */

import { submitKbDraft } from '../lib/kb-workflow.js';
const { useState: useKbState } = React;

/* ---------- tiny markdown renderer ---------- */
function mdInline(s, k) {
  const nodes = []; const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) nodes.push(s.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={k + '-b' + i}>{m[2]}</strong>);
    else nodes.push(<code key={k + '-c' + i} className="md-code">{m[3]}</code>);
    last = re.lastIndex; i++;
  }
  if (last < s.length) nodes.push(s.slice(last));
  return nodes;
}

function MiniMD({ text }) {
  const lines = (text || '').split('\n');
  const out = []; let list = null;
  const flush = () => { if (list) { out.push(<ul key={'ul' + out.length} className="md-ul">{list}</ul>); list = null; } };
  lines.forEach((ln, i) => {
    if (/^### /.test(ln)) { flush(); out.push(<h4 key={i} className="md-h4">{mdInline(ln.slice(4), i)}</h4>); }
    else if (/^## /.test(ln)) { flush(); out.push(<h3 key={i} className="md-h3">{mdInline(ln.slice(3), i)}</h3>); }
    else if (/^# /.test(ln)) { flush(); out.push(<h2 key={i} className="md-h2">{mdInline(ln.slice(2), i)}</h2>); }
    else if (/^> /.test(ln)) { flush(); out.push(<blockquote key={i} className="md-quote">{mdInline(ln.slice(2), i)}</blockquote>); }
    else if (/^---+\s*$/.test(ln)) { flush(); out.push(<hr key={i} className="md-hr" />); }
    else if (/^- /.test(ln)) { list = list || []; list.push(<li key={i}>{mdInline(ln.slice(2), i)}</li>); }
    else if (ln.trim() === '') { flush(); }
    else { flush(); out.push(<p key={i} className="md-p">{mdInline(ln, i)}</p>); }
  });
  flush();
  return <div className="md-body">{out}</div>;
}

/* ---------- line diff (LCS) ---------- */
function kbDiffLines(aText, bText) {
  const a = (aText || '').split('\n'), b = (bText || '').split('\n');
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--)
    dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const rows = []; let i = 0, j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { rows.push({ t: 'ctx', text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { rows.push({ t: 'del', text: a[i] }); i++; }
    else { rows.push({ t: 'add', text: b[j] }); j++; }
  }
  while (i < m) rows.push({ t: 'del', text: a[i++] });
  while (j < n) rows.push({ t: 'add', text: b[j++] });
  return rows;
}

function DiffView({ approved, proposed }) {
  const rows = kbDiffLines(approved, proposed);
  const adds = rows.filter(r => r.t === 'add').length, dels = rows.filter(r => r.t === 'del').length;
  return (
    <div>
      <div className="row gap-8" style={{ marginBottom: 12 }}>
        <span className="chip" style={{ color: 'var(--pass)' }}>+{adds} added</span>
        <span className="chip" style={{ color: 'var(--fail)' }}>−{dels} removed</span>
        {adds + dels === 0 && <span className="muted" style={{ fontSize: 12.5, alignSelf: 'center' }}>No changes yet — edit the draft to see a diff.</span>}
      </div>
      <div className="kb-diff">
        {rows.map((r, i) => (
          <div key={i} className={'kb-diff-row ' + r.t}>
            <span className="kb-diff-gutter">{r.t === 'add' ? '+' : r.t === 'del' ? '−' : ''}</span>
            <span className="kb-diff-text">{r.text || '\u00A0'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- client profile workspace ---------- */
function KBProfile({ slug, back }) {
  const MAV = window.MAV;
  const [profile, setProfile] = useKbState(MAV.kbProfiles[slug]);
  const c = MAV.clientBySlug(slug);
  const [tab, setTab] = useKbState('profile');
  const [draft, setDraft] = useKbState(profile.approvedMarkdown || '');
  const [summary, setSummary] = useKbState('');
  const [submitError, setSubmitError] = useKbState('');
  const [submitting, setSubmitting] = useKbState(false);
  const [loadError, setLoadError] = useKbState('');
  const [loadAttempt, setLoadAttempt] = useKbState(0);
  const actionContext = React.useRef(null);
  actionContext.current = `${MAV.__contextKey || ''}:${slug}`;
  React.useEffect(() => () => { actionContext.current = null; }, []);
  // Seed from any already-pending proposal for this client so a reload (or another
  // dev's open) shows the "Locked · pending review" state instead of an editable
  // draft. Works for both live (hydrated) and mock kbRequests — both carry slug+status.
  const [request, setRequest] = useKbState(() =>
    (MAV.kbRequests || []).find(r => r.slug === slug && r.status === 'pending_review') || null);
  // Lazy-load the full markdown if hydrate only seeded a stub (kbProfiles
  // start with approvedMarkdown='' to keep login fast; full body fetched
  // on profile-open via window.GooseLoadKbProfile).
  React.useEffect(() => {
    if (profile && profile._stub) {
      let alive = true;
      setLoadError('');
      if (!window.GooseLoadKbProfile) { setLoadError('The approved profile is unavailable. Editing is disabled.'); return; }
      window.GooseLoadKbProfile(slug).then((full) => {
        if (!alive) return;
        if (full && !full._stub && typeof full.approvedMarkdown === 'string') {
          setProfile(full); setDraft(full.approvedMarkdown);
        } else {
          setLoadError('The service did not return the approved profile. Editing is disabled.');
        }
      }).catch(e => { if (alive) setLoadError(e.message || 'Could not load the approved profile.'); });
      return () => { alive = false; };
    }
  }, [slug, loadAttempt]);

  const dirty = draft !== profile.approvedMarkdown;
  const changed = kbDiffLines(profile.approvedMarkdown, draft).filter(r => r.t === 'add').map(r => r.text).join('\n');
  const risk = MAV.kbRiskTags(changed);
  const canSubmit = !profile._stub && draft.trim() && summary.trim() && dirty && !request && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    const key = actionContext.current;
    setSubmitting(true); setSubmitError('');
    try {
      const req = await submitKbDraft(window.GooseAPI, { slug, proposedMarkdown: draft, summary,
        baseVersion: profile.versionId, baseMarkdown: profile.approvedMarkdown });
      if (actionContext.current !== key || `${window.MAV.__contextKey || ''}:${slug}` !== key) return;
      MAV.kbRequests = [req, ...((MAV.kbRequests || []).filter(x => x.id !== req.id))];
      setRequest(req); setTab('profile');
    } catch (error) { if (actionContext.current === key) setSubmitError(error.message || 'Could not submit. Your draft is unchanged.'); }
    finally { if (actionContext.current === key) setSubmitting(false); }
  };

  // status banner
  let banner;
  if (profile._stub) banner = { cls: 'run', txt: <>Loading the exact approved profile before editing…</> };
  else if (request) banner = { cls: 'run', txt: <>Submitted <b>{request.id}</b> · awaiting admin review in <b>KB approvals</b>.</> };
  else if (dirty) banner = { cls: 'cond', txt: <>Unsaved local draft — not yet submitted. The canonical profile is unchanged.</> };
  else banner = { cls: 'pass', txt: <>Viewing the approved canonical profile.</> };

  return (
    <div className="page fade-in">
      <div className="page-head" style={{ alignItems: 'flex-start' }}>
        <div>
          <div className="row gap-10" style={{ marginBottom: 8 }}>
            <button className="btn btn-quiet" style={{ padding: 6 }} onClick={back} title="Back to profiles"><I.chevron size={16} style={{ transform: 'rotate(180deg)' }} /></button>
            <ClientBadge slug={slug} size={30} />
            <span className="chip mono" style={{ fontSize: 11 }}>{profile.sourcePath}</span>
          </div>
          <h1 className="page-title" style={{ fontSize: 24 }}>{c.name}</h1>
          <p className="page-desc mono" style={{ fontSize: 12 }}>slug: {slug} · canonical {profile.versionId} · approved by {profile.approvedBy} · {profile.approvedAt}</p>
        </div>
        <span className="pill pill-pass" style={{ flexShrink: 0 }}><span className="dot"></span>Approved</span>
      </div>

      <div className={'kb-banner ' + banner.cls}>{banner.txt}</div>
      {submitError && <div className="kb-banner fail" role="alert">{submitError}</div>}
      {loadError && <div className="kb-banner fail" role="alert">{loadError} <button className="btn" onClick={() => setLoadAttempt(n => n + 1)}>Retry loading</button></div>}

      <div className="tabs" style={{ margin: '16px 0 18px' }}>
        {[['profile', 'Profile'], ['edit', 'Edit draft'], ['diff', 'Diff'], ['history', 'History']].map(([id, l]) => (
          <span key={id} className={'tab' + (tab === id ? ' active' : '')} onClick={() => setTab(id)}>{l}{id === 'diff' && dirty ? ' •' : ''}</span>
        ))}
      </div>

      {tab === 'profile' && (
        <div className="card card-pad kb-paper"><MiniMD text={profile.approvedMarkdown} /></div>
      )}

      {tab === 'edit' && (
        <div className="grid-2-1">
          <div className="card card-pad">
            <div className="sec-head"><span className="sec-title">Markdown draft</span>{request && <span className="chip pill-run" style={{ fontSize: 10.5 }}><span className="dot"></span>Locked · pending review</span>}</div>
            <textarea className="input kb-editor" value={draft} onChange={e => setDraft(e.target.value)} spellCheck={false} disabled={!!request || !!profile._stub || submitting} />
          </div>
          <div className="col gap-16">
            <div className="card card-pad">
              <div className="sec-head"><span className="sec-title">Submit for approval</span></div>
              <label className="field-lbl">Change summary <span className="muted" style={{ fontWeight: 500 }}>· required</span></label>
              <textarea className="input" rows={3} value={summary} onChange={e => setSummary(e.target.value)} disabled={!!request || !!profile._stub || submitting} placeholder="Why are the standards changing? e.g. document the 2× hero-emit fix…" style={{ resize: 'none' }} />
              {risk.length > 0 && (
                <div className="kb-risk mt-12">
                  <div className="row gap-6" style={{ marginBottom: 6 }}><I.warn size={13} style={{ color: 'var(--cond)' }} /><span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cond)' }}>Touches high-impact standards</span></div>
                  <div className="row gap-6 wrap">{risk.map(r => <span key={r} className="chip pill-cond" style={{ fontSize: 10.5 }}>{r}</span>)}</div>
                </div>
              )}
              <button className="btn btn-primary btn-block mt-16" style={{ justifyContent: 'center' }} onClick={submit} disabled={!canSubmit}
                title={request ? 'Already submitted' : !dirty ? 'Make an edit first' : !summary.trim() ? 'Add a change summary' : 'Submit for admin approval'}>
                <I.send size={15} /> {request ? 'Submitted' : 'Submit for approval'}
              </button>
              <p className="muted" style={{ fontSize: 11, marginTop: 9, lineHeight: 1.5 }}>Your edit becomes a pending change request. An independent authorized reviewer approves it before an immutable version is published.</p>
            </div>
            {dirty && !request && (
              <button className="btn btn-ghost" style={{ justifyContent: 'center' }} onClick={() => { setDraft(profile.approvedMarkdown); setSummary(''); }}><I.retry size={14} /> Discard draft</button>
            )}
          </div>
        </div>
      )}

      {tab === 'diff' && (
        <div className="card card-pad"><DiffView approved={profile.approvedMarkdown} proposed={draft} /></div>
      )}

      {tab === 'history' && (
        <div className="card card-pad">
          <div className="sec-head"><span className="sec-title">Approval history</span></div>
          {[
            { v: profile.versionId, by: profile.approvedBy, at: profile.approvedAt, note: 'Current canonical version.' },
            ...(MAV.kbRequests || []).filter(r => r.slug === slug && r.status === 'approved' && r.approvedVersionId && r.approvedVersionId !== profile.versionId)
              .map(r => ({ v: r.approvedVersionId, by: r.reviewedBy, at: r.reviewedAt, note: r.note })),
          ].map((h, i) => (
            <div key={i} className="kb-hist">
              <span className="kb-hist-dot" style={{ background: i === 0 ? 'var(--pass)' : 'var(--faint)' }}></span>
              <div className="grow"><div className="row gap-8"><span className="mono" style={{ fontSize: 12.5, fontWeight: 700 }}>{h.v}</span>{i === 0 && <span className="chip pill-pass" style={{ fontSize: 10 }}>Current</span>}</div><div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{h.note}</div></div>
              <span className="muted mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{h.by} · {h.at}</span>
            </div>
          ))}
          <p className="muted">Showing the current profile and approved proposals loaded in this workspace. No inferred history.</p>
        </div>
      )}
    </div>
  );
}

/* ---------- grid entry point ---------- */
function KBClients({ initialSlug } = {}) {
  const { clients } = window.MAV;
  // initialSlug: global-search deep-link. The parent remounts via key when the target
  // changes, so seeding state here is enough — no effect needed.
  const [open, setOpen] = useKbState(initialSlug || null);
  if (open && !window.MAV.kbProfiles[open]) return <div className="card card-pad" role="status"><h2>Client profile unavailable</h2><p>This profile is not loaded in the current workspace.</p><button className="btn btn-primary" onClick={() => setOpen(null)}>Back to clients</button></div>;
  if (open) return <KBProfile slug={open} back={() => setOpen(null)} />;

  return (
    <div className="page fade-in">
      <div className="page-head">
        <div><h1 className="page-title">Client profiles</h1><p className="page-desc">{clients.length} client profiles · the canonical brand rules, conventions &amp; reference templates the squadron consults. Open one to read or propose edits.</p></div>
        <button className="btn btn-ghost"><I.add size={16} /> New from template</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {clients.map(c => (
          <button key={c.slug} className="card card-pad kb-card" onClick={() => setOpen(c.slug)}>
            <div className="row between" style={{ marginBottom: 14 }}>
              <ClientBadge slug={c.slug} size={42} />
              <span className="chip mono" style={{ fontSize: 11 }}>{c.refs} refs</span>
            </div>
            <div style={{ fontSize: 15.5, fontWeight: 700 }}>{c.name}</div>
            <div className="muted mono" style={{ fontSize: 11.5, marginTop: 2 }}>{c.width}px · {c.font}</div>
            <div style={{ margin: '13px 0' }}><Swatches colors={c.colors} /></div>
            <div className="row gap-6 wrap">
              {c.flags.map(f => <span key={f} className="chip" style={{ fontSize: 10.5 }}>{f}</span>)}
            </div>
            <div className="kb-card-foot"><span className="mono">{c.slug}.md</span><span className="kb-open">Open profile <I.chevron size={13} /></span></div>
          </button>
        ))}
      </div>
    </div>
  );
}

Object.assign(window, { KBClients, MiniMD, kbDiffLines, DiffView });
